import { QueryClient, keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query";
import type { Me } from "@shared/me";
import { api, qs } from "./api";

// TanStack Query owns the data: a page's JSON stays cached for a minute, so
// going back to it is instant, and a changed date range keeps the old page on
// screen until the new one arrives (what the server render did before).

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => {
        const status = (err as { status?: number }).status ?? 0;
        return count < 1 && (status === 0 || status >= 500);
      },
    },
  },
});

export const meQuery = queryOptions({
  queryKey: ["me"],
  queryFn: () => api.get<Me>("/api/me"),
});

/** The signed-in person. The app layout route loads it before anything renders. */
export function useMe(): Me {
  const q = useQuery(meQuery);
  if (!q.data) throw new Error("useMe() called before /api/me loaded");
  return q.data;
}

export type Search = Record<string, string | undefined>;

export function pageQuery<T>(name: string, search: Search) {
  return queryOptions({
    queryKey: ["page", name, search],
    queryFn: () => api.get<T>(`/api/pages/${name}${qs(search)}`),
    placeholderData: keepPreviousData,
  });
}

export function usePage<T>(name: string, search: Search) {
  return useQuery(pageQuery<T>(name, search));
}

/** Re-read everything after a change (what router.refresh() did in Next.js). */
export function refreshAll(): Promise<void> {
  return queryClient.invalidateQueries();
}
